<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, h, computed } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { Grid, type GridCellProps, type GridSortChangeEvent } from '@progress/kendo-vue-grid'
import { Drawer, DrawerItem } from '@progress/kendo-vue-layout'
import { DropDownList } from '@progress/kendo-vue-dropdowns';
import { TextBox as KTextBox, NumericTextBox as KNumericTextBox } from '@progress/kendo-vue-inputs'
import { DatePicker,  type DatePickerChangeEvent } from '@progress/kendo-vue-dateinputs'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import Swal from 'sweetalert2'
import type { MenuSelectEvent } from '@progress/kendo-vue-layout'
import { filterIcon } from '@progress/kendo-svg-icons'

import { GetInvoiceList, CancelInvoiceCart, ResendInvoiceCart } from '../../services/CartService.ts'
import { handleError } from '../../services/ErrorService.ts'
import { GetPartyOrderEntryUrl, GetInvoicePaymentUrl } from '../../stores/SettingsStore.ts'
import { formatDateAsDDMMYYYY } from '../../api/Common.ts'
import { FormatCurrency } from '../../services/currencyFormatter.ts'
import type { IAoeCart } from '../../types/CartTypes.d.ts'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'
import type { SortDescriptor } from '@progress/kendo-data-query'

const logger = useLogger()
setStoreLogger(logger)

interface DDLOption {
  text: string
  id: string
  type?: string
}

// Grid state
const loader = ref<boolean>(false)
const sort = ref<SortDescriptor[]>([{ field: 'invoice.issueDate', dir: 'desc' }])
const gridData = ref<IAoeCart[]>([])
const origGridData = ref<IAoeCart[]>([])
const activeTab = ref('all')

// URLs
const partyOrderEntryURL = ref<string>('')
const invoicePaymentURL = ref<string>('')

// Filters shared
const filterExpanded = ref<boolean>(false)

const invoiceStatusDDL = ref<DDLOption[]>([
  { text: t('dashboard.invoice.title'), id: 'AllOpenOrders' },
  { text: t('dashboard.invoice.AllStatuses'), id: 'All' },
  { text: t('dashboard.invoice.sent'), id: 'ISSUED' },
  { text: t('dashboard.invoice.Read'), id: 'READ' },
  { text: t('dashboard.invoice.Cancelled'), id: 'CANCELED' },
  { text: t('dashboard.invoice.Paid'), id: 'PAID' },
])
const selectedInvoiceStatus = ref<DDLOption>(invoiceStatusDDL.value[0])

const periodDDL = ref<DDLOption[]>([
  { text: t('dashboard.invoice.AllPeriods'), id: 'All' },
  { text: t('dashboard.invoice.Last30Days'), id: '30' },
  { text: t('dashboard.invoice.Last60Days'), id: '60' },
  { text: t('dashboard.invoice.Last90Days'), id: '90' },
])
const selectedPeriod = ref<DDLOption>(periodDDL.value[0])

// Column filter (reuse existing logic but not required for ticket)
const columnDDL = ref<DDLOption[]>([])
const selectedColumn = ref<DDLOption>({ text: '', id: '', type: '' })
const columnOperandDDL = ref<DDLOption[]>([])
const selectedColumnOperand = ref<DDLOption>({ text: '', id: '' })

// Inputs for generic column filters
const searchText = ref<string>('')
const searchNumber = ref<number | undefined>(undefined)
const searchSecondNumber = ref<number | undefined>(undefined)
const txtDate = ref<Date | undefined>(undefined)
const txtSecondDate = ref<Date | undefined>(undefined)

// Inputs for Event Name / ID filters
const searchEventName = ref<string>('')
const searchEventId = ref<string>('')

const filterDrawerItems = ref([
  { text: t('orderList.FilterList'), selected: false, data: { path: '/' } },
])

// Columns definition - compact widths
const columns = [
  { field: 'aoeCartId', title: t('dashboard.invoice.orderNumber'), cell: 'orderNumberTemplate', width: resolveColumnWidth(60, t('dashboard.invoice.orderNumber')) },
  { field: 'showNumber', title: t('dashboard.invoice.eventId'), width: resolveColumnWidth(80, t('dashboard.invoice.eventId')) },
  { field: 'customerName', title: t('dashboard.invoice.customerName'), cell: 'customerNameTemplate', width: resolveColumnWidth(120, t('dashboard.invoice.customerName')) },
  { field: 'invoice.invoiceNumber', title: t('dashboard.invoice.invoiceNumber'), cell: 'invoiceNumberTemplate', width: resolveColumnWidth(160, t('dashboard.invoice.invoiceNumber')) },
  { field: 'read', title: t('dashboard.invoice.read'), width: resolveColumnWidth(70, t('dashboard.invoice.read')) },
  { field: 'total', title: t('dashboard.invoice.amount'), cell: 'amountTemplate', width: resolveColumnWidth(80, t('dashboard.invoice.amount')) },
  { field: 'invoice.issueDate', title: t('dashboard.invoice.issueDate'), cell: 'invoiceIssueDateTemplate', width: resolveColumnWidth(140, t('dashboard.invoice.issueDate')) },
  { field: 'invoice.invoiceStatus.localizedValue', title: t('dashboard.invoice.status'), cell: 'statusTemplate', width: resolveColumnWidth(140, t('dashboard.invoice.status')) },
  { field: 'hints', title: t('dashboard.invoice.hints'), width: resolveColumnWidth(110, t('dashboard.invoice.hints')) },
  { field: '', title: '', width: 220, sortable: false, cell: actionCellFunction },
]

// Lifecycle
onMounted(async () => {
  logger.debug('AoeInvoiceStatus mounted')
  partyOrderEntryURL.value = await GetPartyOrderEntryUrl()
  invoicePaymentURL.value = await GetInvoicePaymentUrl()

  getInvoiceList()
})

// Load data
function getInvoiceList() {
  loader.value = true
  gridData.value = []
  origGridData.value = []
  GetInvoiceList().then((invoiceResp) => {
    if (invoiceResp) {
      console.log(invoiceResp)
      for (const invoice of invoiceResp.items) {
        console.log(invoice)
        // Create a new object with all the original properties plus our custom ones
        const gridItem = {
          ...invoice,
          customerName: invoice.customerFirstName + ' ' + invoice.customerLastName,
          showNumber: invoice.eventId,
          read: invoice.invoice && invoice.invoice.numberOfOpens > 0 ? 1 : 0,
          hints: invoice.eventTitle
        }
        gridData.value.push(gridItem)
        origGridData.value.push(gridItem)
      }

      /* TEAU-1239: Mock data commented out - was for testing only
      const mockData = [
        {
          aoeMasterOrderId: 1001, aoeCartId: 558, beeNumberPayee: 123, payeeFirstName: 'Evan', payeeLastName: 'Christianson', beeBusinessGuidCustomer: 456,
          consultant: { firstName: 'Consultant', lastName: 'One', email: 'c1@test.com', phone: null, alt1Phone: null },
          showNumber: 20031, customerName: 'Evan Christianson',
          invoice: { invoiceNumber: '558F05C5409454', issueDate: '2026-03-20', invoiceStatus: { codeId: 'ISSUED', localizedValue: 'Issued', categoryId: 'STATUS' }, numberOfOpens: 0 },
          total: 56.90, currencyId: 'EUR', read: 0, hints: 'AOE TEST 13 TEST', customerBeeNumber: 123, eventIdToken: 'abc123', aoeCartIdToken: 'token1',
          customerFirstName: 'Evan', customerLastName: 'Christianson', eventId: 20031, eventTitle: 'AOE TEST 13 TEST'
        },
        {
          aoeMasterOrderId: 1002, aoeCartId: 627, beeNumberPayee: 456, payeeFirstName: 'first', payeeLastName: 'last', beeBusinessGuidCustomer: 789,
          consultant: { firstName: 'Consultant', lastName: 'Two', email: 'c2@test.com', phone: null, alt1Phone: null },
          showNumber: 20038, customerName: 'first last',
          invoice: { invoiceNumber: '627CFF92365B3A', issueDate: '2025-11-12', invoiceStatus: { codeId: 'ISSUED', localizedValue: 'Issued', categoryId: 'STATUS' }, numberOfOpens: 0 },
          total: 156.90, currencyId: 'EUR', read: 0, hints: 'test', customerBeeNumber: 456, eventIdToken: 'def456', aoeCartIdToken: 'token2',
          customerFirstName: 'first', customerLastName: 'last', eventId: 20038, eventTitle: 'test'
        },
        {
          aoeMasterOrderId: 1003, aoeCartId: 688, beeNumberPayee: 789, payeeFirstName: 'Amanda', payeeLastName: 'Molley', beeBusinessGuidCustomer: 123,
          consultant: { firstName: 'Consultant', lastName: 'Three', email: 'c3@test.com', phone: null, alt1Phone: null },
          showNumber: 20031, customerName: 'Amanda Molley',
          invoice: { invoiceNumber: '6885723D9E23AB', issueDate: '2026-03-20', invoiceStatus: { codeId: 'ISSUED', localizedValue: 'Issued', categoryId: 'STATUS' }, numberOfOpens: 0 },
          total: 1156.90, currencyId: 'EUR', read: 0, hints: 'AOE TEST 13 TEST', customerBeeNumber: 789, eventIdToken: 'ghi789', aoeCartIdToken: 'token3',
          customerFirstName: 'Amanda', customerLastName: 'Molley', eventId: 20031, eventTitle: 'AOE TEST 13 TEST'
        },
      ] as any
      for (const mock of mockData) {
        gridData.value.push(mock)
        origGridData.value.push(mock)
      }
      */
    }
    loader.value = false
  }).catch(async (error) => {
    await handleError(error, t)
    loader.value = false
  })
}

// Filtering
function applyGridFilter() {
  filterExpanded.value = false
  currentPage.value = 1
  gridData.value = origGridData.value

  // Invoice status
  if (selectedInvoiceStatus.value.id === 'AllOpenOrders') {
    // Filter out CANCELED and PAID invoices for "All open orders"
    gridData.value = gridData.value.filter((item) =>
      item.invoice?.invoiceStatus.codeId?.toLowerCase() !== 'canceled' &&
      item.invoice?.invoiceStatus.codeId?.toLowerCase() !== 'paid'
    )
  } else if (selectedInvoiceStatus.value.id !== 'All') {
    // Filter by specific invoice status
    gridData.value = gridData.value.filter((item) => item.invoice?.invoiceStatus.codeId === selectedInvoiceStatus.value.id)
  }

  // Period
  if (selectedPeriod.value.id !== 'All') {
    const past = new Date()
    past.setDate(past.getDate() - Number(selectedPeriod.value.id))
    gridData.value = gridData.value.filter((item) => {
      const d = new Date(item.invoice?.issueDate as string)
      return d >= past
    })
  }

  // Event name
  if (searchEventName.value.trim().length > 0) {
    gridData.value = gridData.value.filter((item) => (item.eventTitle || '').toLowerCase().includes(searchEventName.value.toLowerCase()))
  }

  // Event ID
  if (searchEventId.value.trim().length > 0) {
    gridData.value = gridData.value.filter((item) => String(item.eventId).includes(searchEventId.value))
  }
}

function clearGridFilter() {
  searchEventName.value = ''
  searchEventId.value = ''
  selectedInvoiceStatus.value = invoiceStatusDDL.value[0]
  selectedPeriod.value = periodDDL.value[0]
  applyGridFilter()
}

// Sorting
function sortChangeHandler(e: GridSortChangeEvent) {
  loader.value = true
  if (e.sort[0] !== undefined) {
    sort.value = e.sort
  }
  loader.value = false
}

// Action buttons
function actionCellFunction(hf: typeof h, tdElement: string | object, props: GridCellProps) {
  const invoice = props.dataItem;
  const isCancelledOrPaid = invoice.invoice?.invoiceStatus.codeId.toLowerCase() === 'cancelled' ||
    invoice.invoice?.invoiceStatus.codeId.toLowerCase() === 'paid';

  const buttons = [];

  // Only show cancel button if invoice is not already cancelled or paid
  if (!isCancelledOrPaid) {
    buttons.push(
      hf(KButton, {
        themeColor: 'secondary',
        size: 'small',
        rounded: 'large',
        fillMode: 'solid',
        class: 'compact-button',
        onClick: () => cancelInvoice(invoice)
      }, () => t('labels.Cancel'))
    );
  }

  // Only show resend button if invoice is not already cancelled or paid
  if (!isCancelledOrPaid) {
    buttons.push(
      hf(KButton, {
        themeColor: 'primary',
        size: 'small',
        rounded: 'large',
        fillMode: 'solid',
        class: 'compact-button',
        onClick: () => resendInvoice(invoice)
      }, () => t('dashboard.invoice.Resend'))
    );
  }

  // Wrap buttons in a div with action-cell-container class for proper centering
  const actionContainer = hf('div', { className: 'action-cell-container' }, buttons);
  return hf(tdElement, { className: 'action-cell' }, actionContainer);
}

function cancelInvoice(invoice: IAoeCart) {
  // Only show cancel button if invoice is not already cancelled or paid
  if (invoice.invoice?.invoiceStatus.codeId.toLowerCase() === 'cancelled' || invoice.invoice?.invoiceStatus.codeId.toLowerCase() === 'paid') {
    Swal.fire({
      title: t('alerts.InvoiceChange'),
      text: t('alerts.CannotCancelInvoice'),
      icon: 'error',
    })
    return
  }

  Swal.fire({
    title: t('alerts.InvoiceChange'),
    text: t('alerts.CancelInvoiceConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('buttons.Yes'),
    cancelButtonText: t('buttons.No'),
  }).then((result) => {
    if (result.isConfirmed) {
      loader.value = true
      // Call the cancel invoice API
      CancelInvoiceCart({ aoeCartIdToken: invoice.aoeCartIdToken })
        .then(() => {
          // Update the invoice status in the grid
          const updatedInvoice = gridData.value.find(item => item.aoeCartIdToken === invoice.aoeCartIdToken)
          Swal.fire(t('alerts.Success'), t('alerts.InvoiceCancelled'), 'success')
          // Refresh the grid to reflect the updated status
          applyGridFilter()
        })
        .catch(async (error) => {
          await handleError(error, t)
        })
        .finally(() => {
          loader.value = false
        })
    }
  })
}

function resendInvoice(invoice: IAoeCart) {
  // Only allow resend for invoices that are not cancelled or paid
  if (invoice.invoice?.invoiceStatus.codeId.toLowerCase() === 'cancelled' || invoice.invoice?.invoiceStatus.codeId.toLowerCase() === 'paid') {
    Swal.fire({
      title: t('alerts.InvoiceChange'),
      text: t('alerts.CannotResendInvoice'),
      icon: 'error',
    })
    return
  }

  Swal.fire({
    title: t('alerts.InvoiceChange'),
    text: t('alerts.ResendInvoiceConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('buttons.Yes'),
    cancelButtonText: t('buttons.No'),
  }).then((result) => {
    if (result.isConfirmed) {
      loader.value = true
      // Call the resend invoice API
      ResendInvoiceCart({ aoeCartIdToken: invoice.aoeCartIdToken })
        .then(() => {
          // Update the invoice status in the grid if needed
          // For resend, we might not need to update the status, but we can refresh the grid
          Swal.fire(t('alerts.Success'), t('alerts.InvoiceResent'), 'success')
        })
        .catch(async (error) => {
          await handleError(error, t)
        })
        .finally(() => {
          loader.value = false
        })
    }
  })
}

// Utility navigation
function viewOrder(event: MouseEvent, customerBeeNumber: number | null, eventIdToken: string) {
  let url = partyOrderEntryURL.value + '?event=' + eventIdToken
  if (customerBeeNumber != null) url += '&customer=' + customerBeeNumber
  window.location.href = url
}

function viewInvoice(event: MouseEvent, invoiceNumber: string) {
  const url = invoicePaymentURL.value + '?invoicenumber=' + invoiceNumber + '&isadmin=true'
  window.location.href = url
}


// Mobile pagination
const currentPage = ref(1)
const itemsPerPage = ref(5)
const paginatedData = computed<any[]>(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return gridData.value.slice(start, end)
})
const totalPages = computed(() => Math.ceil(gridData.value.length / itemsPerPage.value))

function goToPage(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}
function nextPage() { goToPage(currentPage.value + 1) }
function previousPage() { goToPage(currentPage.value - 1) }

</script>

<template>
  <div class="open-orders-container">
    <div class="orders-header">
      <div class="title-section">
        <h2 class="pageHeader">{{ t('dashboard.invoice.allInvoices') }} </h2>
        <p class="pageSubSubHeader">{{ t('dashboard.invoice.description') }}</p>
      </div>

      <div class="tab-navigation">
        <div class="invoice-list-status-filter-div">
          <button :class="['tab-button', activeTab === 'all' ? 'active' : '']" @click="activeTab='all'">{{ t('dashboard.invoice.allInvoices') }}</button>
        </div>
        <div class="filter-button-container">
          <KButton :svg-icon="filterIcon" class="invoice-list-filter-icon" @click="filterExpanded=!filterExpanded">
            {{ t('buttons.Filter') }}
          </KButton>
        </div>
      </div>
    </div>

    <!-- Filter Drawer -->
    <Drawer :expanded="filterExpanded" :position="'end'" :mode="'overlay'" @overlayclick="filterExpanded=false" :items="filterDrawerItems" :item="'CustomItem'" :class="[{ 'd-none': !filterExpanded }, 'invoice-filter-drawer']">
      <template #CustomItem="{ props }">
        <DrawerItem v-bind="props" @click="props.onClick">
          <div class="aoe-cart-detail container">
            <div id="orderListFilterHeader" class="row mt-3">
              <div class="col-12 order-list-filter-header pageHeader">{{ t('dashboard.invoice.GridFilterHeader') }}</div>
            </div>
            <!-- Invoice Status -->
            <div class="row mt-3"><div class="col-12 invoice-list-filters">{{ t('dashboard.invoice.FilterByStatus') }}</div></div>
            <div class="row mt-3"><div class="col-12 invoice-list-filters">
              <DropDownList :data-items="invoiceStatusDDL" v-model="selectedInvoiceStatus" :text-field="'text'" :data-item-key="'id'" />
            </div></div>
            <!-- Period -->
            <div class="row mt-3"><div class="col-12 invoice-list-filters">{{ t('dashboard.invoice.FilterByPeriod') }}</div></div>
            <div class="row mt-3"><div class="col-12 invoice-list-filters">
              <DropDownList :data-items="periodDDL" v-model="selectedPeriod" :text-field="'text'" :data-item-key="'id'" />
            </div></div>
            <!-- Event Name -->
            <div class="row mt-3"><div class="col-12 invoice-list-filters">{{ t('eventList.EventTitle') }}</div></div>
            <div class="row mt-3"><div class="col-12 invoice-list-filters">
              <KTextBox v-model="searchEventName" :placeholder="t('dashboard.invoice.customerName')" />
            </div></div>
            <!-- Event ID -->
            <div class="row mt-3"><div class="col-12 invoice-list-filters">{{ t('eventList.EventId') }}</div></div>
            <div class="row mt-3"><div class="col-12 invoice-list-filters">
              <KTextBox v-model="searchEventId" :placeholder="t('dashboard.invoice.eventId')" />
            </div></div>
            <!-- Buttons -->
            <div class="row mt-3 invoice-list-filter-buttons">
              <div class="col-6 text-center">
                <KButton class="baseBtn primaryBtn invoice-list-button" @click="applyGridFilter">{{ t('orderList.ApplyFilterButton') }}</KButton>
              </div>
              <div class="col-6 text-center">
                <KButton class="baseBtn secondaryBtn invoice-list-button" @click="clearGridFilter">{{ t('orderList.ClearFilterButton') }}</KButton>
              </div>
            </div>
          </div>
        </DrawerItem>
      </template>
    </Drawer>

    <!-- Desktop Grid View -->
    <div class="desktop-grid-view">
      <div id="invoiceListGridRowDiv" class="invoices-grid">
        <!-- No results message -->
        <div v-if="!loader && gridData.length === 0" class="no-invoices-message">
          {{ t('dashboard.invoice.noInvoicesFound') }}
        </div>
        <Grid :data-items="gridData" :resizable="true" :sortable="true" :sort="sort" :pageable="false" :columns="columns" @sortchange="sortChangeHandler" :loader="loader">
          <template #orderNumberTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="invoice-customer">
              <KButton class="base-hyper-link" :fill-mode="'link'" @click="viewOrder($event, props.dataItem.customerBeeNumber, props.dataItem.eventIdToken)">{{ props.dataItem.aoeCartId }}</KButton>
            </td>
          </template>
          <template #customerNameTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="invoice-customer">{{ props.dataItem.customerName }}</td>
          </template>
          <template #invoiceNumberTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="invoice-customer">
              <KButton class="base-hyper-link" :fill-mode="'link'" @click="viewInvoice($event, props.dataItem.invoice?.invoiceNumber)">{{ props.dataItem.invoice?.invoiceNumber }}</KButton>
            </td>
          </template>
          <template #invoiceIssueDateTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="invoice-issue-date-column">
              {{ formatDateAsDDMMYYYY(props.dataItem.invoice?.issueDate) }}
            </td>
          </template>
          <template #statusTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="invoice-status-cell"><span class="status-badge">{{ props.dataItem.invoice?.invoiceStatus.localizedValue }}</span></td>
          </template>
          <template #amountTemplate="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="invoice-amount-cell">{{ FormatCurrency(props.dataItem.total, props.dataItem.currencyId) }}</td>
          </template>
        </Grid>
      </div>
    </div>

    <!-- Mobile Card View -->
    <div class="mobile-card-view">
      <div v-if="loader" class="loading-message">{{ t('labels.Loading') }}</div>
      <div v-else-if="gridData.length === 0" class="no-invoices-message">
        {{ t('dashboard.invoice.noInvoicesFound') }}
      </div>
      <div v-else>
        <div v-for="item in paginatedData" :key="item.aoeCartId" class="invoice-card">
          <div class="invoice-card-header">
            <div class="invoice-card-title-row">
              <KButton class="base-hyper-link invoice-card-link-btn" :fill-mode="'link'" @click="viewOrder($event, item.customerBeeNumber, item.eventIdToken)">
                #{{ item.aoeCartId }}
              </KButton>
            </div>
            <span class="status-badge">{{ item.invoice?.invoiceStatus.localizedValue }}</span>
          </div>
          <div class="invoice-card-body">
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.eventId') }}</span>
              <span class="invoice-card-value">{{ item.showNumber }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.customerName') }}</span>
              <span class="invoice-card-value">{{ item.customerName }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.invoiceNumber') }}</span>
              <span class="invoice-card-value">
                <KButton class="base-hyper-link invoice-card-link-btn" :fill-mode="'link'" @click="viewInvoice($event, item.invoice?.invoiceNumber)">
                  {{ item.invoice?.invoiceNumber }}
                </KButton>
              </span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.read') }}</span>
              <span class="invoice-card-value">{{ item.read }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.amount') }}</span>
              <span class="invoice-card-value">{{ FormatCurrency(item.total, item.currencyId) }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.issueDate') }}</span>
              <span class="invoice-card-value">{{ formatDateAsDDMMYYYY(item.invoice?.issueDate) }}</span>
            </div>
            <div v-if="item.hints" class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.hints') }}</span>
              <span class="invoice-card-value">{{ item.hints }}</span>
            </div>
          </div>
          <div v-if="item.invoice?.invoiceStatus.codeId.toLowerCase() !== 'cancelled' && item.invoice?.invoiceStatus.codeId.toLowerCase() !== 'paid'" class="invoice-card-actions">
            <KButton :theme-color="'secondary'" size="small" :rounded="'large'" :fill-mode="'solid'" @click="cancelInvoice(item)">
              {{ t('labels.Cancel') }}
            </KButton>
            <KButton :theme-color="'primary'" size="small" :rounded="'large'" :fill-mode="'solid'" @click="resendInvoice(item)">
              {{ t('dashboard.invoice.Resend') }}
            </KButton>
          </div>
        </div>
        <div v-if="totalPages > 1" class="mobile-pagination">
          <div class="pagination-info">
            {{ t('rma.Showing') }} {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, gridData.length) }} {{ t('rma.Of') }} {{ gridData.length }}
          </div>
          <div class="pagination-controls">
            <button :disabled="currentPage === 1" @click="goToPage(1)" class="pagination-page-btn pagination-nav-btn" :title="t('rma.FirstPage')">&laquo;</button>
            <button :disabled="currentPage === 1" @click="previousPage" class="pagination-page-btn pagination-nav-btn" :title="t('rma.PreviousPage')">&lt;</button>
            <div class="pagination-current">{{ currentPage }} {{ t('rma.Of') }} {{ totalPages }}</div>
            <button :disabled="currentPage === totalPages" @click="nextPage" class="pagination-page-btn pagination-nav-btn" :title="t('rma.NextPage')">&gt;</button>
            <button :disabled="currentPage === totalPages" @click="goToPage(totalPages)" class="pagination-page-btn pagination-nav-btn" :title="t('rma.LastPage')">&raquo;</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
/* Simplified styles copied from Dashboard component */
.open-orders-container { color: #333; padding: $card-padding; padding-top: 5px; background-color: var(--vt-c-white); border-radius: $radius-md; box-shadow: 0 10px 15px rgba(black, 0.1); }
.orders-header { margin-bottom: 20px; }
.title-section { margin-bottom: 15px; }
.open-orders-container .pageHeader { color: var(--primary-text-color, var(--vt-c-teal-dark)); letter-spacing: 0.5px; }
.pageSubSubHeader { @include typography-small; color: #555; }
.order-count { @include typography-small; @include font-weight-regular; color: #666; background-color: transparent; padding: 2px 8px; border-radius: 12px; margin-left: 10px; border: 1px solid #e0e0e0; }
.tab-navigation { display: flex; border-bottom: 2px solid var(--primary-text-color, var(--vt-c-teal-dark)); margin-top: 20px; justify-content: space-between; padding-bottom: 0; }
.tab-button { @include typography-small; padding: 10px 20px; background: none; border: none; cursor: pointer; color: #666; position: relative; transition: color .2s; }
.tab-button.active { @include font-weight-medium; color: var(--vt-c-teal-dark); }
.tab-button.active::after { content: ''; position: absolute; bottom: -1px; left: 0; width: 100%; height: 2px; background-color: var(--vt-c-teal-dark); }
.invoice-status-cell {
  text-align: center !important;
  vertical-align: middle !important;
}

.status-badge {
  @include typography-small;
  @include font-weight-medium;
  display: inline-block !important;
  padding: $spacing-2 $spacing-3 !important;
  border-radius: $radius-sm !important;
  background-color: var(--vt-c-divider-light-1) !important;
  color: var(--color-text) !important;
  min-height: 28px !important;
  line-height: 1.2 !important;
  text-align: center !important;
  text-transform: none !important;
  border: none !important;
  box-shadow: none !important;
}
.invoices-grid { margin-top: 20px; overflow-x: auto; }
.invoice-list-filter-icon { background: none; border: none; display: flex; align-items: center; gap: 6px; }
.filter-button-container { display: flex; justify-content: flex-end; }
.action-cell-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 30px;
  width: 100%;
  gap: $spacing-1;
}

/* Style for the action cell */
.action-cell {
  padding: 0 4px !important;
  min-width: 250px !important;
}

/* Style for table cells containing action buttons */
.k-grid td {
  vertical-align: middle;
}

/* Ensures the table has sufficient row height */
.k-grid tr {
  min-height: 40px;
}

/* Compact buttons for action cells - consistent height for 2 rows */
.compact-button {
  @include typography-small;
  padding: 6px 12px !important;
  height: 48px !important;
  line-height: 1.4 !important;
  white-space: normal !important;
  text-transform: none !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: $radius-md !important;
}

.action-cell .compact-button {
  flex: 1;
  white-space: normal !important;
  text-align: center;
}

/* Prevent column header truncation - single line display */
.invoices-grid .k-grid-header .k-column-title {
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: clip !important;
}

.no-invoices-message { @include typography-body; text-align: center; padding: 30px; color: #666; background-color: var(--vt-c-white-soft); border-radius: $radius-sm; margin-bottom: 20px; }

/* Mobile Card View */
.desktop-grid-view {
  display: block;
}

.mobile-card-view {
  display: none;
}

.invoice-card {
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  overflow: hidden;
  margin-bottom: $spacing-4;
  transition: box-shadow 0.2s ease;
}

.invoice-card:hover {
  box-shadow: 0 4px 12px rgba(black, 0.1);
}

.invoice-card-header {
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: $spacing-2-5;
}

.invoice-card-title-row {
  display: flex;
  align-items: center;
  gap: $gap-sm;
}

.invoice-card-link-btn {
  @include typography-small;
  padding: 0 !important;
  min-height: auto !important;
}

.invoice-card-body {
  padding: $spacing-4;
}

.invoice-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: $spacing-2 0;
  border-bottom: 1px solid var(--vt-c-white-soft);
  gap: $spacing-2-5;
}

.invoice-card-row:last-child {
  border-bottom: none;
}

.invoice-card-label {
  @include typography-small;
  @include font-weight-medium;
  color: #666;
  flex-shrink: 0;
}

.invoice-card-value {
  @include typography-small;
  color: #333;
  text-align: right;
  word-break: break-word;
}

.invoice-card-actions {
  display: flex;
  gap: $spacing-2;
  padding: $spacing-3 $spacing-4;
  border-top: 1px solid #e0e0e0;
  background-color: var(--vt-c-white-soft);
  justify-content: flex-end;
}

.mobile-pagination {
  margin-top: $spacing-6;
  padding-top: $spacing-4;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  @include typography-small;
  color: #666;
  text-align: center;
  margin-bottom: $spacing-4;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-2;
  flex-wrap: wrap;
}

.pagination-current {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
}

.pagination-page-btn {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 36px;
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-page-btn:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--vt-c-teal-dark);
}

.pagination-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.loading-message {
  @include typography-body;
  text-align: center;
  padding: $spacing-10 $spacing-5;
  color: #666;
}

/* !important required: Override Kendo's inline style width with responsive min() — matches the proven cart-summary drawer pattern, keeps a visible/clickable overlay strip at all breakpoints */
.invoice-filter-drawer .k-drawer-wrapper {
  width: min(80vw, 420px) !important;
}

@media (max-width: 768px) {
  .desktop-grid-view {
    display: none;
  }

  .mobile-card-view {
    display: block;
  }


  /* Stack filter buttons vertically on mobile to prevent overlap */
  .invoice-list-filter-buttons .col-6 {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
    margin-bottom: $spacing-2;
  }

  .invoice-list-filter-buttons .col-6:last-child {
    margin-bottom: 0;
  }
}
</style>
